import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  menuPermissions: string[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  hasMenuPermission: (menu: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [menuPermissions, setMenuPermissions] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Check if token is still valid
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiryTime = payload.exp * 1000;
          const currentTime = Date.now();

          if (currentTime > expiryTime) {
            // Token expired, remove it
            localStorage.removeItem('token');
            setUser(null);
          } else {
            // Token still valid, try to get user data from API
            try {
              const userData = await authService.getCurrentUser();
              setUser(userData);

              // Load menu permissions
              try {
                const permissions = await authService.getMenuPermissions();
                setMenuPermissions(permissions);
              } catch (permError) {
                console.log('Failed to load menu permissions');
                setMenuPermissions([]);
              }
            } catch (apiError) {
              // API failed, use token data as fallback
              console.log('API call failed, using token data');
              setUser({
                id: payload.userId,
                email: payload.email,
                username: payload.email, // Use email as fallback for username
                firstName: payload.firstName || '',
                lastName: payload.lastName || '',
                Role: { id: '', name: payload.role, description: '', permissions: [] },
                accountId: payload.accountId || '',
                isActive: true,
                createdAt: new Date(),
                updatedAt: new Date()
              });
              // For fallback, assume super_admin has all permissions
              if (payload.role === 'super_admin') {
                setMenuPermissions(['dashboard', 'tracking', 'orders', 'metrics', 'users', 'roles', 'configs', 'lambdas', 'queues']);
              }
            }
          }
        } catch (error) {
          localStorage.removeItem('token');
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Declare logout and refreshToken before useEffect
  const logout = useCallback(() => {
    authService.logout();
    localStorage.removeItem('token');
    setUser(null);
    setMenuPermissions([]);
    navigate('/login');
  }, [navigate]);

  const refreshToken = useCallback(async () => {
    const response = await authService.refreshToken();
    localStorage.setItem('token', response.token);
  }, []);

  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = localStorage.getItem('token');
      if (token && user) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiryTime = payload.exp * 1000;
          const currentTime = Date.now();
          const timeUntilExpiry = expiryTime - currentTime;

          // Refresh token when less than 1 day remaining
          if (timeUntilExpiry < 24 * 60 * 60 * 1000) {
            refreshToken().catch(error => {
              console.error('Token refresh failed:', error);
              logout();
            });
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    };

    // Check token every 6 hours instead of 30 minutes
    const refreshInterval = setInterval(checkTokenExpiry, 6 * 60 * 60 * 1000);

    // Check immediately on mount
    checkTokenExpiry();

    return () => clearInterval(refreshInterval);
  }, [user, logout, refreshToken]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      localStorage.setItem('token', response.token);
      setUser(response.user);

      // Load menu permissions after login
      try {
        const permissions = await authService.getMenuPermissions();
        setMenuPermissions(permissions);
      } catch (error) {
        console.log('Failed to load menu permissions after login');
        setMenuPermissions([]);
      }

      navigate('/dashboard');
    } catch (error) {
      // Re-throw error so Login.tsx can handle it
      throw error;
    }
  };

  const hasMenuPermission = (menu: string) => {
    return menuPermissions.includes(menu);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        menuPermissions,
        login,
        logout,
        refreshToken,
        hasMenuPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};